import React, { type PropsWithChildren, useMemo } from 'react';
import { GrowthBookProvider as BaseGrowthBookProvider, GrowthBookPayload, TrackingCallback } from '@growthbook/growthbook-react';
import { GrowthBook } from '@growthbook/growthbook-react';
import configJSON from 'config.json';
import { ENVIRONMENT_PRODUCTION, getEnvironment, isEndToEndTest } from 'utils/environment';
import { AnalyticsController } from 'analytics/analytics-controller';
import Cookies from 'js-cookie';
import { AREA_PAGE_FOURTH_MAP_CARD_COOKIE_NAME, MAP_SINGLE_LISTING_PREVIEW_COOKIE_NAME } from 'constants/cookies';
import { AREA_PAGE_FOURTH_MAP_CARD_FLAG, MAP_SINGLE_LISTING_PREVIEW_FLAG } from 'constants/feature-flags';
import { isClientSide } from 'utils/host-config';
import { TenantId } from '@tenants/types';

type GrowthBookProviderProps = PropsWithChildren<{
  /** Device ID for anonymous user tracking. */
  deviceId: string;
  /** Optional logged in user identification */
  loggedInId?: string | number;
  /** Optional tenant ID for tenant-specific targeting */
  tenantId?: TenantId;
  /** Optional GrowthBook payload from server-side rendering for hydration */
  payload?: GrowthBookPayload;
}>;

/**
 * Tracking callback for GrowthBook experiment views.
 *
 * This function is called whenever a user is exposed to an experiment variation.
 * It logs the experiment details and tracks the event through the analytics system.
 *
 * @param experiment - The experiment object containing experiment metadata
 * @param result - The experiment result containing variation information
 */
const onExperimentViewed: TrackingCallback = (experiment, result) => {
  const experimentId = experiment.key;
  const variationId = result.key;

  console.debug('Viewed Experiment', { experimentId, variationId });

  AnalyticsController.trackGrowthbookEvent(experiment.key, result.variationId);
};

/**
 * A wrapper around the GrowthBook React provider for client-side feature flag management.
 *
 * This component initializes a GrowthBook instance with user attributes and feature definitions,
 * enabling client-side feature flag evaluation and experiment tracking. It supports both
 * server-side rendered payloads and client-side initialization.
 *
 * @param children React children components that will have access to GrowthBook context
 * @param user Optional user object containing user identification and attributes
 * @param deviceId Optional device ID for anonymous user tracking
 * @param payload Optional GrowthBook payload from server-side rendering for hydration
 *
 * @example
 * ```tsx
 * // Basic usage without SSR
 * <GrowthBookProvider loggedInId={user?.id} deviceId={deviceId}>
 *   <App />
 * </GrowthBookProvider>
 *
 * // With SSR payload
 * <GrowthBookProvider loggedInId={user?.id} deviceId={deviceId} payload={growthBookPayload}>
 *   <App />
 * </GrowthBookProvider>
 * ```
 */
export const GrowthBookProvider = ({ children, loggedInId, deviceId, tenantId, payload }: GrowthBookProviderProps) => {
  // Create a singleton GrowthBook instance for the app
  const growthbook = useMemo(
    () => {
      const gb = new GrowthBook({
        apiHost: configJSON.growthbook.apiHost,
        clientKey: configJSON.growthbook.clientKey,
        enableDevMode: getEnvironment() !== ENVIRONMENT_PRODUCTION,
        trackingCallback: onExperimentViewed,
        subscribeToChanges: true,
        enabled: !isEndToEndTest,
        attributes: { 
          id: deviceId, 
          anonymous_id: deviceId, 
          loggedIn: loggedInId,
          tenantId: tenantId,
        },
      }).initSync({
        payload,
      });

      if (isClientSide()) {
        // Set cookies based on feature flag evaluations
        const showMapCardExperimentView = gb.getFeatureValue(AREA_PAGE_FOURTH_MAP_CARD_FLAG, 0) == 1;
        Cookies.set(AREA_PAGE_FOURTH_MAP_CARD_COOKIE_NAME, showMapCardExperimentView.toString());

        // Set cookie for map single listing preview experiment
        const showMapSingleListingPreview = gb.getFeatureValue(MAP_SINGLE_LISTING_PREVIEW_FLAG, 0) == 1;
        Cookies.set(MAP_SINGLE_LISTING_PREVIEW_COOKIE_NAME, showMapSingleListingPreview.toString());
      }
      return gb;
    },
    [payload, deviceId, loggedInId, tenantId]
  );

  return (
    <BaseGrowthBookProvider growthbook={growthbook}>
      {children}
    </BaseGrowthBookProvider>
  );
};